import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable } from '../../components/DataTable';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  useCreateOrganization,
  useDisableOrganization,
  useEnableOrganization,
  useOrganizations,
} from '../../hooks/useOrganizations';
import { formatDate } from '../../lib/format';
import type { Organization } from '../../types/domain';

/** Mismo criterio que valida el backend en routes/organizations.ts. */
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function OrgList() {
  const navigate = useNavigate();
  const { data: orgs, isLoading } = useOrganizations();
  const disableMutation = useDisableOrganization();
  const enableMutation = useEnableOrganization();
  const createMutation = useCreateOrganization();

  const [disableTarget, setDisableTarget] = useState<Organization | null>(null);
  const [enableTarget, setEnableTarget] = useState<Organization | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'individual' | 'team'>('individual');
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newEmail, setNewEmail] = useState('');

  function handleDisableConfirm() {
    if (!disableTarget) return;
    disableMutation.mutate(disableTarget.id, { onSuccess: () => setDisableTarget(null) });
  }

  function handleEnableConfirm() {
    if (!enableTarget) return;
    enableMutation.mutate(enableTarget.id, { onSuccess: () => setEnableTarget(null) });
  }

  // Los tres campos son obligatorios: la organización sin correo no recibe las
  // notificaciones de cambios, y las columnas son NOT NULL (migración 007).
  const canCreate =
    newName.trim() !== '' && newIdentifier.trim() !== '' && EMAIL_PATTERN.test(newEmail.trim());

  function resetCreateForm() {
    setNewName('');
    setNewType('individual');
    setNewIdentifier('');
    setNewEmail('');
  }

  function handleCreate() {
    if (!canCreate) return;
    createMutation.mutate(
      {
        name: newName.trim(),
        type: newType,
        identifier: newIdentifier.trim(),
        email: newEmail.trim().toLowerCase(),
      },
      {
        onSuccess: () => {
          setShowCreate(false);
          resetCreateForm();
        },
      }
    );
  }

  const columns = [
    {
      header: 'Nombre',
      accessor: (row: Organization) => (
        <span className={row.disabled ? 'text-muted-foreground' : undefined}>{row.name}</span>
      ),
    },
    {
      header: 'Tipo',
      accessor: (row: Organization) => (
        <Badge variant="secondary">{row.type === 'individual' ? 'Individual' : 'Equipo'}</Badge>
      ),
    },
    {
      header: 'Estado',
      accessor: (row: Organization) =>
        row.disabled ? (
          <Badge variant="destructive">Deshabilitada</Badge>
        ) : (
          <Badge variant="success">Activa</Badge>
        ),
    },
    {
      header: 'Creada',
      accessor: (row: Organization) => formatDate(row.created_at),
    },
    {
      header: 'Acciones',
      accessor: (row: Organization) => (
        <div onClick={(e) => e.stopPropagation()}>
          {row.disabled ? (
            <Button size="sm" variant="outline" onClick={() => setEnableTarget(row)}>
              Habilitar
            </Button>
          ) : (
            <Button size="sm" variant="destructive" onClick={() => setDisableTarget(row)}>
              Deshabilitar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Organizaciones</h1>
        <Button onClick={() => setShowCreate(true)}>Nueva organización</Button>
      </div>

      <DataTable
        columns={columns}
        data={orgs ?? []}
        isLoading={isLoading}
        emptyMessage="No hay organizaciones."
        getRowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/organizations/${row.id}`)}
      />

      <ConfirmDialog
        open={disableTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDisableTarget(null);
        }}
        title="Deshabilitar organización"
        description={`¿Deshabilitar "${disableTarget?.name}"? Sus miembros no podrán acceder.`}
        onConfirm={handleDisableConfirm}
        confirmLabel="Deshabilitar"
        isDestructive
        isPending={disableMutation.isPending}
      />

      <ConfirmDialog
        open={enableTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEnableTarget(null);
        }}
        title="Habilitar organización"
        description={`¿Habilitar "${enableTarget?.name}"?`}
        onConfirm={handleEnableConfirm}
        confirmLabel="Habilitar"
        isPending={enableMutation.isPending}
      />

      <Dialog
        open={showCreate}
        onOpenChange={(open) => {
          setShowCreate(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva organización</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="org-name">Nombre</Label>
              <Input
                id="org-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nombre de la organización"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-type">Tipo</Label>
              <Select value={newType} onValueChange={(v) => setNewType(v as 'individual' | 'team')}>
                <SelectTrigger id="org-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="team">Equipo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-identifier">NIT o RUT</Label>
              <Input
                id="org-identifier"
                value={newIdentifier}
                onChange={(e) => setNewIdentifier(e.target.value)}
                placeholder="900123456-7"
                maxLength={50}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-email">Correo de notificaciones</Label>
              <Input
                id="org-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="contacto@organizacion.com"
                maxLength={320}
              />
              <p className="text-xs text-muted-foreground">
                A esta dirección llegan las alertas de cambios en los procesos.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreate(false)}
              disabled={createMutation.isPending}
            >
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending || !canCreate}>
              {createMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
